/**
 * Centralized Error Handling Service for BeCreatives Customer Frontend
 * 
 * This service provides:
 * - Error classification and categorization
 * - User-friendly message generation
 * - Retry logic with exponential backoff
 * - Error reporting and logging
 * - Integration with toast notifications
 */

import { Logger } from 'src/utils/logger';
import { toast } from 'src/components/ui/use-toast';
import { HttpError } from 'src/api/Http/HttpError';
import i18n from 'src/services/i18n/i18n';
import {
  ErrorCode,
  ErrorCategory,
  ErrorDefinition,
  ERROR_DEFINITIONS,
  HTTP_STATUS_TO_ERROR_CODE,
  getErrorDefinition,
  isRetryableError,
} from './error-types';

const logger = Logger.createScopedLogger('ErrorHandling');

/**
 * Processed error with all metadata for handling
 */
export interface ProcessedError {
  code: ErrorCode;
  category: ErrorCategory;
  title: string;
  message: string;
  originalError: unknown;
  httpStatus?: number;
  isRetryable: boolean;
  isRecoverable: boolean;
  validationErrors?: Record<string, string[]>;
  timestamp: Date;
}

/**
 * Options for error handling
 */
export interface ErrorHandlingOptions {
  showToast?: boolean;
  customMessage?: string;
  customTitle?: string;
  context?: string;
  suppressLog?: boolean;
  onRetry?: () => Promise<void>;
}

/**
 * Retry configuration
 */
export interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 30000,
  backoffMultiplier: 2,
};

/**
 * Error Handling Service
 */
class ErrorHandlingService {
  private retryAttempts: Map<string, number> = new Map();

  /**
   * Process any error into a standardized format
   */
  processError(error: unknown, options: ErrorHandlingOptions = {}): ProcessedError {
    const timestamp = new Date();
    
    // Handle HttpError from our API client
    if (error instanceof HttpError || this.isHttpErrorLike(error)) {
      return this.processHttpError(error as HttpError, timestamp, options);
    }

    // Handle Axios errors directly
    if (this.isAxiosError(error)) {
      return this.processAxiosError(error, timestamp, options);
    }

    // Handle native Error
    if (error instanceof Error) {
      return this.processNativeError(error, timestamp, options);
    }

    // Handle unknown error types
    return this.processUnknownError(error, timestamp, options);
  }

  /**
   * Handle an error with full processing and side effects
   */
  handleError(error: unknown, options: ErrorHandlingOptions = {}): ProcessedError {
    const processed = this.processError(error, options);

    // Log the error
    if (!options.suppressLog) {
      this.logError(processed, options.context);
    }

    // Show toast notification if appropriate
    const definition = getErrorDefinition(processed.code);
    const shouldShowToast = options.showToast ?? definition.handling.showToast;
    
    if (shouldShowToast && processed.code !== ErrorCode.CANCELLED) {
      this.showErrorToast(processed, options);
    }

    return processed;
  }

  /**
   * Execute a function with automatic retry on failure
   */
  async withRetry<T>(
    fn: () => Promise<T>,
    retryKey: string,
    config: Partial<RetryConfig> = {}
  ): Promise<T> {
    const fullConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
    let lastError: unknown;
    let attempt = 0;

    while (attempt <= fullConfig.maxRetries) {
      try {
        const result = await fn();
        // Success - clear retry count
        this.retryAttempts.delete(retryKey);
        return result;
      } catch (error) {
        lastError = error;
        const processed = this.processError(error);

        // Check if error is retryable
        if (!processed.isRetryable || attempt >= fullConfig.maxRetries) {
          this.retryAttempts.delete(retryKey);
          throw error;
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          fullConfig.baseDelay * Math.pow(fullConfig.backoffMultiplier, attempt),
          fullConfig.maxDelay
        );

        logger.info(`Retry attempt ${attempt + 1}/${fullConfig.maxRetries} for ${retryKey} in ${delay}ms`);
        
        // Wait before retry
        await this.sleep(delay);
        attempt++;
        this.retryAttempts.set(retryKey, attempt);
      }
    }

    // Cleanup on exhausted retries
    this.retryAttempts.delete(retryKey);
    throw lastError;
  }

  /**
   * Get current retry count for a key
   */
  getRetryCount(retryKey: string): number {
    return this.retryAttempts.get(retryKey) || 0;
  }

  /**
   * Clear retry count for a key
   */
  clearRetryCount(retryKey: string): void {
    this.retryAttempts.delete(retryKey);
  }

  /**
   * Check if network is online
   */
  isOnline(): boolean {
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  }

  /**
   * Get error code from HTTP status
   */
  getErrorCodeFromStatus(status: number): ErrorCode {
    return HTTP_STATUS_TO_ERROR_CODE[status] || ErrorCode.UNKNOWN;
  }

  /**
   * Get error definition
   */
  getDefinition(code: ErrorCode): ErrorDefinition {
    return getErrorDefinition(code);
  }

  /**
   * Check if error should trigger redirect
   */
  shouldRedirect(processed: ProcessedError): { redirect: boolean; path?: string } {
    const definition = getErrorDefinition(processed.code);
    return {
      redirect: definition.handling.shouldRedirect,
      path: definition.handling.redirectPath,
    };
  }

  // Private helper methods

  private processHttpError(
    error: HttpError,
    timestamp: Date,
    options: ErrorHandlingOptions
  ): ProcessedError {
    const status = typeof error.code === 'number' ? error.code : parseInt(String(error.code), 10) || 0;
    const errorCode = this.getErrorCodeFromStatus(status);
    const definition = getErrorDefinition(errorCode);

    // Extract validation errors if present
    let validationErrors: Record<string, string[]> | undefined;
    if (error.errors?.form) {
      validationErrors = error.errors.form;
    }

    return {
      code: errorCode,
      category: definition.category,
      title: options.customTitle || this.getTranslatedTitle(definition),
      message: options.customMessage || this.extractUserMessage(error) || this.getTranslatedMessage(definition),
      originalError: error,
      httpStatus: status,
      isRetryable: isRetryableError(errorCode),
      isRecoverable: definition.handling.isRecoverable,
      validationErrors,
      timestamp,
    };
  }

  private processAxiosError(
    error: any,
    timestamp: Date,
    options: ErrorHandlingOptions
  ): ProcessedError {
    const status = error.response?.status || 0;
    
    // Check for network errors
    if (error.code === 'ERR_NETWORK' || !this.isOnline()) {
      const definition = getErrorDefinition(ErrorCode.NETWORK_OFFLINE);
      return {
        code: ErrorCode.NETWORK_OFFLINE,
        category: ErrorCategory.NETWORK,
        title: options.customTitle || this.getTranslatedTitle(definition),
        message: options.customMessage || this.getTranslatedMessage(definition),
        originalError: error,
        httpStatus: 0,
        isRetryable: true,
        isRecoverable: true,
        timestamp,
      };
    }

    // Check for timeout
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      const definition = getErrorDefinition(ErrorCode.NETWORK_TIMEOUT);
      return {
        code: ErrorCode.NETWORK_TIMEOUT,
        category: ErrorCategory.TIMEOUT,
        title: options.customTitle || this.getTranslatedTitle(definition),
        message: options.customMessage || this.getTranslatedMessage(definition),
        originalError: error,
        httpStatus: 408,
        isRetryable: true,
        isRecoverable: true,
        timestamp,
      };
    }

    // Check for cancelled request
    if (error.code === 'ERR_CANCELED') {
      const definition = getErrorDefinition(ErrorCode.CANCELLED);
      return {
        code: ErrorCode.CANCELLED,
        category: ErrorCategory.CLIENT,
        title: this.getTranslatedTitle(definition),
        message: this.getTranslatedMessage(definition),
        originalError: error,
        isRetryable: false,
        isRecoverable: false,
        timestamp,
      };
    }

    // Process as HTTP error
    const errorCode = this.getErrorCodeFromStatus(status);
    const definition = getErrorDefinition(errorCode);

    return {
      code: errorCode,
      category: definition.category,
      title: options.customTitle || this.getTranslatedTitle(definition),
      message: options.customMessage || error.response?.data?.message || this.getTranslatedMessage(definition),
      originalError: error,
      httpStatus: status,
      isRetryable: isRetryableError(errorCode),
      isRecoverable: definition.handling.isRecoverable,
      validationErrors: error.response?.data?.errors?.form,
      timestamp,
    };
  }

  private processNativeError(
    error: Error,
    timestamp: Date,
    options: ErrorHandlingOptions
  ): ProcessedError {
    // Check for specific error types
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      const definition = getErrorDefinition(ErrorCode.NETWORK_ERROR);
      return {
        code: ErrorCode.NETWORK_ERROR,
        category: ErrorCategory.NETWORK,
        title: options.customTitle || this.getTranslatedTitle(definition),
        message: options.customMessage || this.getTranslatedMessage(definition),
        originalError: error,
        isRetryable: true,
        isRecoverable: true,
        timestamp,
      };
    }

    const definition = getErrorDefinition(ErrorCode.UNKNOWN);
    return {
      code: ErrorCode.UNKNOWN,
      category: ErrorCategory.UNKNOWN,
      title: options.customTitle || this.getTranslatedTitle(definition),
      message: options.customMessage || error.message || this.getTranslatedMessage(definition),
      originalError: error,
      isRetryable: false,
      isRecoverable: true,
      timestamp,
    };
  }

  private processUnknownError(
    error: unknown,
    timestamp: Date,
    options: ErrorHandlingOptions
  ): ProcessedError {
    const definition = getErrorDefinition(ErrorCode.UNKNOWN);
    
    // Try to extract message from unknown error
    let message = definition.defaultMessage;
    if (typeof error === 'string') {
      message = error;
    } else if (error && typeof error === 'object' && 'message' in error) {
      message = String((error as any).message);
    }

    return {
      code: ErrorCode.UNKNOWN,
      category: ErrorCategory.UNKNOWN,
      title: options.customTitle || this.getTranslatedTitle(definition),
      message: options.customMessage || message,
      originalError: error,
      isRetryable: false,
      isRecoverable: true,
      timestamp,
    };
  }

  private extractUserMessage(error: HttpError): string | null {
    // If message contains a pipe, it has title|message format
    if (error.message.includes('|')) {
      const parts = error.message.split('|');
      return parts[1] || parts[0];
    }
    
    // Don't return generic/technical messages
    const genericMessages = [
      'Unknown error',
      'Request failed',
      'Network Error',
      'Internal Server Error',
    ];
    
    if (genericMessages.some(msg => error.message.toLowerCase().includes(msg.toLowerCase()))) {
      return null;
    }

    return error.message;
  }

  private showErrorToast(processed: ProcessedError, options: ErrorHandlingOptions): void {
    toast({
      variant: 'destructive',
      title: processed.title,
      description: processed.message,
    });
  }

  private logError(processed: ProcessedError, context?: string): void {
    const logData = {
      code: processed.code,
      category: processed.category,
      httpStatus: processed.httpStatus,
      isRetryable: processed.isRetryable,
      context,
      timestamp: processed.timestamp.toISOString(),
    };

    if (processed.category === ErrorCategory.VALIDATION) {
      logger.debug('Validation error', logData);
    } else if (processed.code === ErrorCode.CANCELLED) {
      logger.debug('Request cancelled', logData);
    } else {
      logger.error(processed.message, logData);
    }
  }

  private isHttpErrorLike(error: unknown): boolean {
    return (
      error !== null &&
      typeof error === 'object' &&
      'message' in error &&
      'code' in error
    );
  }

  private isAxiosError(error: unknown): boolean {
    return (
      error !== null &&
      typeof error === 'object' &&
      'isAxiosError' in error &&
      (error as any).isAxiosError === true
    );
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get translated title using i18n, falling back to default
   */
  private getTranslatedTitle(definition: ErrorDefinition): string {
    const key = `${definition.translationKey}.title`;
    const translated = i18n.t(key, { defaultValue: '' });
    return translated || definition.defaultTitle;
  }

  /**
   * Get translated message using i18n, falling back to default
   */
  private getTranslatedMessage(definition: ErrorDefinition): string {
    const key = `${definition.translationKey}.message`;
    const translated = i18n.t(key, { defaultValue: '' });
    return translated || definition.defaultMessage;
  }
}

// Export singleton instance
export const errorHandlingService = new ErrorHandlingService();

// Export class for testing
export { ErrorHandlingService };
